import _ from "lodash";
import axios from "axios";
import cookies from "js-cookie";
import api from "@/api";
import config from "@/config";

const initialState = () => ({
  auth_token: {},
  user: {},
  permissions: {},
  keep_me_logged_in: false
});

export default {
  namespaced: true,
  state: initialState(),
  getters: {
    is_authenticated: state => !_.isEmpty(state.auth_token),
    user_id: state => _.get(state, "user.id", null),
    full_name: state => _.get(state, "user.full_name", null),
    email: state => _.get(state, "user.email", null),
    user_role: state => _.get(state, "user.role.formatted", null),
    user_avatar: state => _.get(state, "user.avatar", null),
    user_initials: state => _.get(state, "user.full_name_initials", null),
    // "commercial" only if this User was auto-created by a Commercial ->
    // Residential SSO crossover; null for a native residential user. Read
    // before logout clears state, to decide where the redirect sends them.
    sso_origin: state => _.get(state, "user.sso_origin", null),
    is_client: state =>
      _.get(state, "user.account.classification") === "client",
    is_pzse: state => _.get(state, "user.account.classification") === "pzse",
    is_vendor_electrical: state =>
      _.get(state, "user.account.classification") === "vendor-electrical",
    is_vendor_designer: state =>
      _.get(state, "user.account.classification") === "vendor-designer",
    company_name: state => _.get(state, "user.account.company_name", null),
    is_engineer: state =>
      _.get(state, "user.role.value", "") === "pzse-engineer",
    is_pzse_admin: state =>
      _.get(state, "user.role.value", "") === "pzse-admin",
    is_vendor: state => _.get(state, "user.role.value").indexOf("vendor") >= 0,
    is_coordinator: state =>
      _.get(state, "user.role.value", "") === "pzse-coordinator",
    is_client_manager: state =>
      _.get(state, "user.role.value", "") === "client-manager",
    is_client_admin: state =>
      _.get(state, "user.role.value", "") === "client-admin",
    is_client_user: state =>
      _.get(state, "user.role.value", "") === "client-user",
    is_user_pzse: state =>
      _.get(state, "user.role.value", "").indexOf("pzse") >= 0,
    // Single source of truth for "can this role land on / see the SLA
    // Dashboard" - used by the post-login redirect, the route access guard,
    // and the sidebar nav, so the role list can't drift between them again.
    can_view_dashboard: (state, getters) =>
      getters.is_client_admin ||
      getters.is_client_manager ||
      getters.is_coordinator ||
      getters.is_pzse_admin,
    // Whether this account's admin has already connected the company to
    // Commercial - see AccountResource::has_commercial_org on the backend.
    has_commercial_org: state =>
      !!_.get(state, "user.account.has_commercial_org"),
    // Single source of truth for "can this role use the Commercial <-> Residential
    // toggle" - client roles only (mirrors the backend's is_client() role gate),
    // and only the admin may be the one to connect the company for the first
    // time; a manager/user can only join a link the admin already made, matching
    // GenerateCommercialRedirectService's own server-side rule.
    can_toggle_commercial: (state, getters) =>
      getters.is_client_admin ||
      ((getters.is_client_manager || getters.is_client_user) &&
        getters.has_commercial_org),
    is_super_admin: state =>
      _.get(state, "user.role.value").indexOf("super-admin") >= 0,
    account_id: state => _.get(state, "user.account_id", null),
    account_logo: state => _.get(state, "user.account.company_logo", null),
    custom_pricings: state => _.get(state, "user.custom_pricings", null),
    account_setting: state => _.get(state, "user.account_setting", null),
    is_vendor_engineer: state =>
      _.get(state, "user.role.value", "") === "vendor-engineer",
    is_vendor_admin: state =>
      _.get(state, "user.role.value", "") === "vendor-admin"
  },
  mutations: {
    SET_KEEP_ME_LOGGED_IN: (state, payload) =>
      (state.keep_me_logged_in = payload),
    SET_AUTH_TOKEN: (state, auth_token) => {
      if (!auth_token) {
        return;
      }

      // validateAuthentication and CommercialBridge both rehydrate by
      // reading the cookie, parsing it, and committing it straight back --
      // that round-trip must not touch the cookie at all, or every route
      // navigation would reset its expiry to today's keep_me_logged_in
      // (always false on a fresh page load, never persisted) and silently
      // cap a "keep me logged in" session at 1 day instead of 7. Only a
      // genuinely new token (a fresh login, or an SSO exchange that may be
      // replacing a completely different account's session) should write.
      const incoming = JSON.stringify(auth_token);

      if (cookies.get("auth_token") !== incoming) {
        let expires = 1;
        if (state.keep_me_logged_in) {
          expires = 7;
        }

        cookies.set("auth_token", incoming, { expires: expires });
      }

      state.auth_token = auth_token;
    },
    INVALIDATE_AUTH_TOKEN: state => {
      // cookies beyond session should not get deleted
      // when invalidating token or logging out
      const cookies_beyond_session = [
        "patch_noshow",
        "patch_count",
        "tour_dontshow"
      ];

      _.each(cookies.get(), (item, index) => {
        const persists = cookies_beyond_session.some(cookie => {
          return index.indexOf(cookie) > -1;
        });

        if (persists) return;

        cookies.remove(index);
      });

      _.each(initialState(), (item, index) => (state[index] = item));
    },
    SET_USER: (state, payload) => {
      cookies.set("user", JSON.stringify(payload));

      state.user = payload;
    },
    SET_USER_PORTAL_TOUR_STATUS: (state, payload) => {
      state.user.portal_tour_status = payload;
    },
    SET_USER_PORTAL_TOUR_STEP: (state, payload) => {
      state.user.portal_tour_step = payload;
    },
    SET_PERMISSIONS: (state, payload) => {
      cookies.set("permissions", JSON.stringify(payload));

      state.permissions = payload;
    }
  },
  actions: {
    login: async ({ commit }, payload) => {
      // eslint-disable-next-line no-useless-catch
      try {
        const authentication_response = await api.post("/auth/login", payload);
        const { access_token, expires_in, token_type, refresh_token } = _.get(
          authentication_response,
          "data.result"
        );

        commit("SET_AUTH_TOKEN", {
          access_token,
          expires_in,
          token_type,
          refresh_token
        });

        const current_user_response = await api.get("/users/me");
        const user = _.get(current_user_response, "data.result");
        const permissions = _.get(current_user_response, "data.permissions");

        commit("SET_USER", user);

        commit("SET_PERMISSIONS", permissions);

        return _.concat(authentication_response, current_user_response);
      } catch (error) {
        throw error;
      }
    },
    currentUser: async ({ commit }) => {
      // eslint-disable-next-line no-useless-catch
      try {
        const current_user_response = await api.get("/users/me");
        const user = _.get(current_user_response, "data.result");
        const permissions = _.get(current_user_response, "data.permissions");

        commit("SET_USER", user);

        commit("SET_PERMISSIONS", permissions);

        return current_user_response;
      } catch (error) {
        throw error;
      }
    },
    logout: async ({ commit }) => {
      try {
        return await api.post("/auth/logout");
        // eslint-disable-next-line no-useless-catch
      } catch (error) {
        throw error;
      } finally {
        commit("INVALIDATE_AUTH_TOKEN");
      }
    },
    // Flow 2 (Residential -> Commercial): ask the backend to generate a
    // one-time link into commercial.pzse.com for the current user.
    redirectToCommercial: async () => {
      // eslint-disable-next-line no-useless-catch
      try {
        const response = await api.post("/sso/commercial-redirect");

        return _.get(response, "data.result.redirect_url");
      } catch (error) {
        throw error;
      }
    },
    // Flow 1 (Commercial -> Residential): exchange the Supabase access token
    // handed off by commercial.pzse.com for a real residential session, then
    // hydrate the user/permissions exactly like a normal login does. This
    // hits a PUBLIC endpoint outside /api/internal, so it bypasses the
    // pre-configured `api` client and calls the backend's raw origin instead.
    exchangeCommercialToken: async ({ commit }, { access_token }) => {
      // eslint-disable-next-line no-useless-catch
      try {
        // Derive the backend origin from app_api_request_url (always a full,
        // correctly-scheme'd URL, unlike app_api_url) rather than assuming
        // a second env var is formatted the same way.
        const api_origin = config.app_api_request_url.replace(
          /\/api\/internal\/?$/,
          ""
        );

        const exchange_response = await axios.post(
          `${api_origin}/api/sso/commercial/exchange`,
          { access_token }
        );

        const {
          access_token: issued_access_token,
          expires_in,
          token_type,
          refresh_token
        } = _.get(exchange_response, "data.result");

        commit("SET_AUTH_TOKEN", {
          access_token: issued_access_token,
          expires_in,
          token_type,
          refresh_token
        });

        const current_user_response = await api.get("/users/me");
        const user = _.get(current_user_response, "data.result");
        const permissions = _.get(current_user_response, "data.permissions");

        commit("SET_USER", user);

        commit("SET_PERMISSIONS", permissions);

        return current_user_response;
      } catch (error) {
        throw error;
      }
    }
  }
};
